import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, tap, throwError } from 'rxjs';
import { ResetPasswordValidators } from 'src/app/share/class/reset-password-validators';
import { UserDataService } from 'src/app/share/data-service/user.data.service';
import { InfoDialogComponent } from 'src/app/share/dialog/info-dialog/info-dialog.component';
import { CreatePasswordPayloadInterface } from 'src/app/share/interface/user.interface';
import { LoadingService } from 'src/app/share/service/loading.service';
import { PartnerService } from 'src/app/share/service/partner.service';

@Component({
  selector: 'app-account-activation',
  templateUrl: './account-activation.component.html',
  styleUrls: ['./account-activation.component.scss']
})
export class AccountActivationComponent implements OnInit {

  formGroup: UntypedFormGroup;
  hide: boolean = true;
  partnerCode: string;

  newPassLength: boolean = false;
  repeatNewPassLength: boolean = false;
  expired: boolean = false;
  success: boolean = false;
  key_activation: any;


  name: string;
  email: string;
  mobileNo: string;
  

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private userDataService: UserDataService,
    private activatedRoute: ActivatedRoute,
	  private translateService: TranslateService,
	  private partnerService: PartnerService,
    private loadingService: LoadingService
  ) {
		this.partnerCode = this.partnerService.getPartnerCode();
		if(!this.partnerCode) {
			this.partnerCode = "ps";
		} 
  }

   // convenience getter for easy access to form controls
	get f() {
		return this.formGroup.controls;
	}

	get requiredValid() {
		return !this.formGroup.controls["newPassword"].hasError(
			"required",
		);
	}

	get isCarsome() {
		return this.partnerCode == 'carsome';
	}


  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
		this.key_activation = params.key;
		this.name = params.name;
		this.email = params.email;
		this.mobileNo = params.mobileNo;
		this.check_url_validation(this.key_activation);
	});

	if (this.success && !this.expired) {
		this.openSuccessDialog();
	} else if (this.expired) {
		this.openErrorDialog();
	}


    this.formGroup = new UntypedFormGroup({
      newPassword: new UntypedFormControl(
        null, 
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          ResetPasswordValidators.patternValidator(
            new RegExp("(?=.*[0-9])"),
            {
              requiresDigit: true,
            },
          ),
          ResetPasswordValidators.patternValidator(
            new RegExp("(?=.*[a-z])"),
            {
              requiresLowercase: true,
            },
          ),
          ResetPasswordValidators.patternValidator(
            new RegExp("(?=.*[$@^!%*?&])"),
            {
              requiresSpecialChars: true,
            },
          ),
        ]),
      ),
      repeatNewPassword: new UntypedFormControl(null, [Validators.required, Validators.minLength(8)]),
    },
    {
      validators: ResetPasswordValidators.MatchValidator,
    });
  }

  activateAccount(){
	this.loadingService.loadingOn(true)
	let new_password = this.formGroup.get("newPassword").value;
	let confirmedNewPassword = this.formGroup.get(
		"repeatNewPassword",
	).value;

	let objRequest: CreatePasswordPayloadInterface = {
		newPassword: encodeURIComponent(new_password),
		confirmNewPassword: encodeURIComponent(confirmedNewPassword),
		key: this.key_activation,
	};

    this.userDataService.createPassword(objRequest)
		.pipe(
			catchError((err) => {
				this.openErrorDialog();
				return throwError(err);
			}),	
			tap((x) => {
				this.success = true;
				this.openSuccessDialog();
			}),
			finalize(() =>	this.loadingService.loadingOff(true))
		)
		.subscribe();
  }

  openErrorDialog(type?: string) {
	let accountActivated = type == "activated";

    const dialogRef = this.dialog.open(InfoDialogComponent, {
      panelClass: "dialog-container",
			data: {
				img: "assets/image/invalid-icon.png",
				text: accountActivated ? 
				this.translateService.instant('error.accountIsActivated') 
					: 
				this.translateService.instant('error.accountIsInvalid') 
			},
	});

	dialogRef.afterClosed().subscribe(() => {
		// Navigate to login page
    if(accountActivated) {
			if(this.partnerCode == 'ps'){
				this.router.navigate(["/login"]);
			} else {
				this.router.navigate(["/login"], { queryParams: { partnerCode: this.partnerCode } });
			}
		}
	});
  }

  private openSuccessDialog() {
		const dialogRef = this.dialog.open(InfoDialogComponent, {
			panelClass: "dialog-container",
			data: {
				img: "assets/image/success-icon.png",
				text: this.translateService.instant('error.accountSuccessActivated'),
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			// Navigate to login page
			if(this.partnerCode == 'ps'){
				this.router.navigate(["/login"]);
			} else {
				this.router.navigate(["/login"], { queryParams: { partnerCode: this.partnerCode } });
			}		
		});
	}

  	getNewPasswordLength(value: any) {
		if (value.length < 8) {
			this.newPassLength = false;
		} else {
			this.newPassLength = true;
		}
	}

	getRepeatNewPasswordLength(value: any) {
		if (value.length < 8) {
			this.repeatNewPassLength = false;
		} else {
			this.repeatNewPassLength = true;
		}
	}

  	check_url_validation(key?: any) {
		let encodedKey = decodeURIComponent(key);
		this.userDataService.createPasswordLinkVerify(encodedKey).subscribe({
			next: (x) => {
				if (x) {
					this.expired = false;
				} else {
					this.expired = true;
				}
			},
			error: (err) => {
				if(err.error == "Account already activated"){
					this.openErrorDialog("activated");
				} else {
					this.openErrorDialog();
				}
			},
		});
	}

}
